// ============================================================================
// PÁGINA: ESCOLAS ASSOCIADAS - VERSÃO CORRIGIDA
// ============================================================================
// Arquivo: src/pages/EscolasAssociadas.tsx
// Descrição: Busca de escolas credenciadas com filtros client-side
// ============================================================================

import React, { useState, useEffect, useMemo } from "react";
import {
  MapPin,
  Search,
  Filter,
  ChevronDown,
  X,
  ExternalLink,
  Loader2,
  AlertCircle,
  Navigation,
} from "lucide-react";

// ============================================================================
// TIPOS E INTERFACES
// ============================================================================

interface Escola {
  id: string;
  schoolName: string;
  city: string;
  state: string;
  address: string;
  logoUrl?: string;
  website?: string;
  latitude?: number;
  longitude?: number;
}

interface Filtros {
  busca: string;
  estado: string;
  cidade: string;
  useProximidade: boolean;
}

interface StateCount {
  state: string;
  count: number;
}

interface CityCount {
  city: string;
  count: number;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const EscolasAssociadas: React.FC = () => {
  const [todasEscolas, setTodasEscolas] = useState<Escola[]>([]);
  const [filtros, setFiltros] = useState<Filtros>({
    busca: "",
    estado: "",
    cidade: "",
    useProximidade: false,
  });
  const [mostrarFiltros, setMostrarFiltros] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  // ============================================================================
  // CARREGAR TODAS AS ESCOLAS
  // ============================================================================

  useEffect(() => {
    carregarTodasEscolas();
  }, []);

  const carregarTodasEscolas = async () => {
    setLoading(true);
    setError(null);

    try {
      const API_BASE = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:4000/api/v1';
      const res = await fetch(`${API_BASE}/affiliates/public?isSchool=true&limit=2000`);
      
      if (!res.ok) {
        throw new Error('Falha ao carregar escolas');
      }

      const data = await res.json();
      const lista: Escola[] = (data.affiliates ?? []).map((a: any) => ({
        id: a.id || a._id || Math.random().toString(),
        schoolName: a.schoolName || a.name || 'Escola sem nome',
        city: a.city || '',
        state: a.state || '',
        address: a.address || '',
        logoUrl: a.logoUrl || a.logo || '',
        website: a.website || '',
        latitude: a.latitude || 0,
        longitude: a.longitude || 0,
      }));

      setTodasEscolas(lista);
    } catch (err) {
      console.error("Erro ao carregar escolas:", err);
      setError("Não foi possível carregar as escolas. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // CALCULAR ESTADOS E CIDADES DISPONÍVEIS
  // ============================================================================

  const estadosDisponiveis = useMemo((): StateCount[] => {
    const map: Record<string, number> = {};
    todasEscolas.forEach((escola) => {
      if (escola.state) {
        map[escola.state] = (map[escola.state] || 0) + 1;
      }
    });
    return Object.entries(map)
      .map(([state, count]) => ({ state, count }))
      .sort((a, b) => a.state.localeCompare(b.state));
  }, [todasEscolas]);

  const cidadesDisponiveis = useMemo((): CityCount[] => {
    if (!filtros.estado) return [];

    const map: Record<string, number> = {};
    todasEscolas
      .filter((escola) => escola.state === filtros.estado)
      .forEach((escola) => {
        if (escola.city) {
          map[escola.city] = (map[escola.city] || 0) + 1;
        }
      });

    return Object.entries(map)
      .map(([city, count]) => ({ city, count }))
      .sort((a, b) => a.city.localeCompare(b.city));
  }, [todasEscolas, filtros.estado]);

  // ============================================================================
  // CALCULAR DISTÂNCIA (Haversine formula)
  // ============================================================================

  const calcularDistancia = (
    lat1: number,
    lon1: number,
    lat2: number,
    lon2: number
  ): number => {
    const R = 6371; // Raio da Terra em km
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ============================================================================
  // APLICAR FILTROS
  // ============================================================================

  const escolasFiltradas = useMemo(() => {
    let resultado = [...todasEscolas];

    // Filtro por busca de texto
    if (filtros.busca.trim()) {
      const q = filtros.busca.trim().toLowerCase();
      resultado = resultado.filter(
        (escola) =>
          escola.schoolName.toLowerCase().includes(q) ||
          escola.city.toLowerCase().includes(q) ||
          escola.address.toLowerCase().includes(q)
      );
    }

    // Filtro por estado
    if (filtros.estado) {
      resultado = resultado.filter((escola) => escola.state === filtros.estado);
    }

    // Filtro por cidade
    if (filtros.cidade) {
      resultado = resultado.filter((escola) => escola.city === filtros.cidade);
    }

    // Filtro por proximidade
    if (filtros.useProximidade && userLocation) {
      resultado = resultado
        .map((escola) => ({
          ...escola,
          distance: escola.latitude && escola.longitude
            ? calcularDistancia(
                userLocation.latitude,
                userLocation.longitude,
                escola.latitude,
                escola.longitude
              )
            : 9999,
        }))
        .filter((escola) => escola.distance <= 100)
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 50);
    }

    return resultado;
  }, [todasEscolas, filtros, userLocation]);

  // ============================================================================
  // HANDLERS
  // ============================================================================

  const handleFiltroChange = (campo: keyof Filtros, valor: string | boolean) => {
    setFiltros((prev) => {
      const novos = { ...prev, [campo]: valor };
      if (campo === "estado") novos.cidade = "";
      return novos;
    });
  };

  const limparFiltros = () => {
    setFiltros({
      busca: "",
      estado: "",
      cidade: "",
      useProximidade: false,
    });
    setUserLocation(null);
  };

  const usarMinhaLocalizacao = async () => {
    if (!navigator.geolocation) {
      setError("Geolocalização não suportada pelo navegador.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      const location = {
        latitude: position.coords.latitude,
        longitude: position.coords.longitude,
      };

      setUserLocation(location);
      setFiltros((prev) => ({ ...prev, useProximidade: true }));
    } catch (err) {
      console.error("Erro ao obter localização:", err);
      setError("Não foi possível obter sua localização. Verifique as permissões.");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // ESTATÍSTICAS
  // ============================================================================
const totalEstados = "25+";
const totalEscolas = "3,5 Mil";
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-orange-50/30">
      {/* ============================================================================ */}
      {/* HERO SECTION */}
      {/* ============================================================================ */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-cosmos-800 via-cosmos-600 to-cosmos-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-gold-500 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-gold-400 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-3 px-5 py-2.5 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
              <img
                src="/icons/icon6.png"
                alt="Escolas"
                className="w-6 h-6 object-contain"
              />
              <span className="text-sm font-bold text-gold-300 uppercase tracking-wider">
                Rede Nacional
              </span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black mb-4 font-luxury">
              Escolas Associadas
            </h1>

            <p className="text-base md:text-lg text-white/85 max-w-3xl mx-auto mb-8">
              Encontre escolas credenciadas próximas a você em todo o Brasil.
            </p>

            {/* STATS */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/15">
                <p className="text-3xl font-black text-gold-300 mb-1">
                  {totalEscolas}
                </p>
                <p className="text-sm text-white/75">Escolas Credenciadas</p>
              </div>
              <div className="bg-white/10 backdrop-blur-md rounded-2xl p-5 border border-white/15">
                <p className="text-3xl font-black text-gold-300 mb-1">
                  {totalEstados}
                </p>
                <p className="text-sm text-white/75">Estados Atendidos</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================================ */}
      {/* BUSCA E FILTROS */}
      {/* ============================================================================ */}
      <section className="py-12">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            {/* HEADER BUSCA */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    value={filtros.busca}
                    onChange={(e) => handleFiltroChange("busca", e.target.value)}
                    placeholder="Buscar por nome ou cidade..."
                    className="w-full pl-12 pr-4 py-4 border-2 border-gray-200 rounded-2xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all text-base md:text-lg bg-white shadow-sm"
                  />
                </div>

                <button
                  onClick={usarMinhaLocalizacao}
                  className="px-6 py-4 bg-gradient-gold text-white font-bold rounded-2xl hover:shadow-gold transition-all flex items-center justify-center gap-2 whitespace-nowrap"
                  disabled={loading}
                >
                  <Navigation className="w-5 h-5" />
                  Usar Minha Localização
                </button>

                <button
                  onClick={() => setMostrarFiltros(!mostrarFiltros)}
                  className="px-6 py-4 bg-cosmos-800 text-white font-bold rounded-2xl hover:shadow-lg transition-all flex items-center justify-center gap-2"
                >
                  <Filter className="w-5 h-5" />
                  Filtros
                  <ChevronDown
                    className={`w-5 h-5 transition-transform ${
                      mostrarFiltros ? "rotate-180" : ""
                    }`}
                  />
                </button>
              </div>
            </div>

            {/* PAINEL FILTROS */}
            {mostrarFiltros && (
              <div className="bg-white rounded-3xl shadow-xl p-6 mb-8 border border-gold-200">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Estado (UF)
                    </label>
                    <select
                      value={filtros.estado}
                      onChange={(e) => handleFiltroChange("estado", e.target.value)}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Todos os Estados</option>
                      {estadosDisponiveis.map((est) => (
                        <option key={est.state} value={est.state}>
                          {est.state} ({est.count} escolas)
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Cidade
                    </label>
                    <select
                      value={filtros.cidade}
                      onChange={(e) => handleFiltroChange("cidade", e.target.value)}
                      disabled={!filtros.estado}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-gold-500 focus:border-transparent transition-all disabled:opacity-50 bg-white"
                    >
                      <option value="">Todas as Cidades</option>
                      {cidadesDisponiveis.map((cidade) => (
                        <option key={cidade.city} value={cidade.city}>
                          {cidade.city} ({cidade.count} escolas)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between">
                  <div className="text-sm text-gray-600">
                    <span className="font-bold text-gold-700">
                      {escolasFiltradas.length}
                    </span>{" "}
                    escolas encontradas
                  </div>

                  <div className="flex gap-3">
                    <button
                      onClick={limparFiltros}
                      className="px-4 py-2 text-gray-700 hover:text-gray-900 font-semibold flex items-center gap-2"
                    >
                      <X className="w-4 h-4" />
                      Limpar
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* INFO PROXIMIDADE */}
            {filtros.useProximidade && userLocation && (
              <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-center gap-3">
                <Navigation className="w-5 h-5 text-blue-600 flex-shrink-0" />
                <p className="text-sm text-blue-900">
                  Mostrando escolas próximas à sua localização (até 100km)
                </p>
              </div>
            )}

            {/* RESULTADOS COUNT */}
            {!mostrarFiltros && !loading && (
              <div className="mb-6 flex items-center justify-between">
                <p className="text-base md:text-lg text-gray-700">
                  <span className="font-bold text-gold-700">
                    {escolasFiltradas.length}
                  </span>{" "}
                  escolas encontradas
                </p>
              </div>
            )}

            {/* ============================================================================ */}
            {/* LOADING */}
            {/* ============================================================================ */}
            {loading && (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 text-gold-500 animate-spin mb-4" />
                <p className="text-gray-600 text-lg">Carregando escolas...</p>
              </div>
            )}

            {/* ============================================================================ */}
            {/* ERROR */}
            {/* ============================================================================ */}
            {error && !loading && (
              <div className="bg-red-50 border border-red-200 rounded-2xl p-6 flex items-start gap-4">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-red-900 mb-1">Erro</h3>
                  <p className="text-red-700">{error}</p>
                </div>
              </div>
            )}

            {/* LISTA DE ESCOLAS */}
            {!loading && !error && (
              <div className="grid gap-6">
                {escolasFiltradas.map((escola) => (
                  <div
                    key={escola.id}
                    className="bg-white rounded-3xl shadow-lg hover:shadow-xl transition-all p-6 md:p-8 border border-gold-100"
                  >
                    <div className="grid lg:grid-cols-3 gap-6">
                      {/* INFO PRINCIPAL */}
                      <div className="lg:col-span-2">
                        <div className="mb-5">
                          {/* Logo + Nome */}
                          <div className="flex items-center gap-4 mb-4">
                            {escola.logoUrl ? (
                              <img
                                src={escola.logoUrl}
                                alt={escola.schoolName}
                                className="w-16 h-16 rounded-xl object-cover border border-amber-200 flex-shrink-0"
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                            ) : (
                              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center flex-shrink-0 border border-amber-200">
                                <span className="text-2xl font-black text-amber-600">
                                  {escola.schoolName.charAt(0)}
                                </span>
                              </div>
                            )}
                            <h3 className="text-2xl md:text-3xl font-black text-cosmos-800 font-luxury">
                              {escola.schoolName}
                            </h3>
                          </div>

                          {/* ENDEREÇO */}
                          <div className="flex items-start gap-3">
                            <div className="w-10 h-10 rounded-2xl bg-gold-50 border border-gold-200 flex items-center justify-center flex-shrink-0">
                              <MapPin className="w-5 h-5 text-gold-700" />
                            </div>
                            <div className="text-gray-700">
                              <p className="font-medium">{escola.address}</p>
                              <p className="text-gray-600">
                                {escola.city} - {escola.state}
                              </p>
                              {filtros.useProximidade && (escola as any).distance > 0 && (
                                <p className="text-sm text-gold-700 font-semibold mt-1">
                                  📍 {(escola as any).distance.toFixed(1)} km de distância
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* AÇÕES */}
                      <div className="flex flex-col gap-3">
                        {escola.website && (
                          <a
                            href={escola.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-3 bg-amber-600 text-white font-bold rounded-2xl hover:bg-amber-700 transition-all text-center flex items-center justify-center gap-2"
                          >
                            <ExternalLink className="w-4 h-4" />
                            Visitar Filiado
                          </a>
                        )}

                        {(escola.city || escola.address) && (
                          <a
                            href={`https://www.google.com/maps/search/${encodeURIComponent(
                              [escola.address, escola.city, escola.state]
                                .filter(Boolean)
                                .join(', ')
                            )}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="px-4 py-3 bg-gradient-gold text-white font-bold rounded-2xl hover:shadow-gold transition-all text-center flex items-center justify-center gap-2"
                          >
                            <MapPin className="w-4 h-4" />
                            Ver no Mapa
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* ============================================================================ */}
            {/* NENHUM RESULTADO */}
            {/* ============================================================================ */}
            {!loading && !error && escolasFiltradas.length === 0 && (
              <div className="text-center py-16">
                <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-2xl font-black text-cosmos-800 mb-2 font-luxury">
                  Nenhuma escola encontrada
                </h3>
                <p className="text-gray-600 mb-6">
                  Tente ajustar os filtros de busca.
                </p>
                <button
                  onClick={limparFiltros}
                  className="px-6 py-3 bg-gradient-gold text-white font-bold rounded-2xl hover:shadow-gold transition-all"
                >
                  Limpar Filtros
                </button>
              </div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
};

export default EscolasAssociadas;